import React, { useState, useRef, useEffect } from 'react';
import './SearchableDropdown.css';

interface SearchableDropdownProps {
    options: Array<{ id: number; label: string }>;
    value: number | null;
    onChange: (id: number) => void;
    placeholder?: string;
    disabled?: boolean;
    label?: string;
    required?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Auswählen...',
    disabled = false,
    label,
    required = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedOption = options.find(opt => opt.id === value);
    
    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                setTimeout(() => {
                    inputRef.current?.focus();
                }, 100);
            }
        }
    };

    const handleSelect = (id: number) => {
        onChange(id);
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        if (!isOpen) {
            setIsOpen(true);
        }
    };

    return (
        <div className="form-group">
            {label && (
                <label>
                    {label} {required && <span className="required">*</span>}
                </label>
            )}
            <div 
                className={`searchable-dropdown ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
                ref={dropdownRef}
            >
                <div 
                    className="dropdown-header"
                    onClick={handleToggle}
                >
                    <input
                        ref={inputRef}
                        type="text"
                        className="dropdown-search-input"
                        placeholder={selectedOption ? selectedOption.label : placeholder}
                        value={searchQuery}
                        onChange={handleInputChange}
                        onFocus={() => !disabled && setIsOpen(true)}
                        disabled={disabled}
                    />
                    <span className="dropdown-arrow">▼</span>
                </div>
                
                {isOpen && (
                    <div className="dropdown-list">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <div
                                    key={option.id}
                                    className={`dropdown-item ${option.id === value ? 'selected' : ''}`}
                                    onClick={() => handleSelect(option.id)}
                                >
                                    {option.label}
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-item no-results">
                                Keine Ergebnisse gefunden
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchableDropdown;
